import { useTranslation } from '@/hooks/useTranslation';
import { LucideIcon } from 'lucide-react';
import React from 'react';

interface EmptyStateProps {
  /**
   * Custom icon component to display
   */
  icon?: LucideIcon;
  /**
   * Translation key for the title. If the translation is not found, falls back to the title prop
   */
  title?: string;
  /**
   * Translation key for the description. If the translation is not found, falls back to the description prop
   */
  description?: string;
  /**
   * Custom icon size
   */
  iconSize?: 'sm' | 'md' | 'lg';
  /**
   * Minimum height for the empty state container
   */
  minHeight?: string;
  /**
   * Additional custom actions to display below the description
   */
  actions?: React.ReactNode;
  /**
   * Custom className for additional styling
   */
  className?: string;
}

/**
 * Reusable empty state component with customizable icon, title, description, and actions
 */
export const EmptyState: React.FC<EmptyStateProps> = React.memo(
  ({
    icon: Icon,
    title,
    description,
    iconSize = 'md',
    minHeight = 'auto',
    actions,
    className = '',
  }) => {
    const { tDynamic } = useTranslation();

    const iconSizes = {
      sm: 'h-8 w-8',
      md: 'h-12 w-12',
      lg: 'h-16 w-16',
    };

    const displayTitle = tDynamic(title);
    const displayDescription = tDynamic(description);

    return (
      <div className={`text-center py-8 ${className}`} style={{ minHeight }}>
        {Icon && (
          <Icon
            className={`${iconSizes[iconSize]} mx-auto text-muted-foreground mb-4`}
          />
        )}
        {displayTitle && (
          <h3 className='text-lg font-semibold mb-2'>{displayTitle}</h3>
        )}
        {displayDescription && (
          <p className='text-muted-foreground mb-4'>{displayDescription}</p>
        )}
        {actions && <div className='mt-4'>{actions}</div>}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export default EmptyState;
